import { Suspense, lazy, useEffect, useState } from "react";

import { getCardBorderStyle } from "@/app/application/theme";
import { Translate } from "@/app/application/Translate";
import { MeasurementContext } from "@/features/measurements/context/selectedMeasurementContext";
import { ManualMeasurementPointSummary } from "@/features/measurements/model/types/MeasurementGroups";
import DashboardGrid from "@/shared/ui/Dashboard/DashboardGrid";
import ComponentError from "@/shared/ui/errors/ComponentError";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import TuneIcon from "@mui/icons-material/Tune";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

const MeasurementChartsContainer = lazy(
	() =>
		import("@/features/measurements/ui/components/charts/MeasurementChartsContainer"),
);

const ButtonContainer = lazy(
	() => import("@/features/measurements/ui/components/buttons/ButtonContainer"),
);

const MeasurementGroupList = lazy(
	() =>
		import("@/features/measurements/ui/components/file-tree/MeasurementGroupList"),
);
const RecordsTable = lazy(
	() =>
		import("@/features/measurements/ui/components/records-table/MeasurementsRecordsTable"),
);
const EditMeasurementSkeleton = lazy(
	() =>
		import("@/features/measurements/ui/components/skeletons/EditMeasurementSkeleton"),
);

const EditMeasurement = lazy(
	() =>
		import("@/features/measurements/ui/components/edit-measurement/EditMeasurement"),
);

type Props = {
	initialSelectedMeasurement?: ManualMeasurementPointSummary | null;
	/** When true, hides the page header and uses compact padding (e.g. inside modal) */
	isEmbedded?: boolean;
};

const getPanelSx = (theme: Theme) => ({
	bgcolor: "background.paper",
	borderRadius: 3,
	border: getCardBorderStyle(theme),
	boxShadow:
		theme.palette.mode === "dark"
			? `0 0 0 1px ${alpha(theme.palette.common.white, 0.03)}`
			: "0 1px 3px rgba(15,23,42,0.06)",
	height: "100%",
	minHeight: 140,
	overflow: "hidden",
	transition: "border-color 0.2s ease, box-shadow 0.2s ease",
	"&:hover": {
		borderColor: alpha(theme.palette.primary.main, 0.3),
		boxShadow:
			theme.palette.mode === "dark"
				? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`
				: "0 2px 6px rgba(0,0,0,0.06)",
	},
});

const EmptyStatePlaceholder = ({
	icon: Icon,
	title,
	subtitle,
	theme,
}: {
	icon: React.ElementType;
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	theme: Theme;
}) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			minHeight: 140,
			py: 3,
			px: 2,
			textAlign: "center",
		}}
	>
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: 56,
				height: 56,
				borderRadius: "50%",
				bgcolor:
					theme.palette.mode === "dark"
						? alpha(theme.palette.primary.main, 0.1)
						: alpha(theme.palette.primary.main, 0.06),
				color: "primary.main",
				mb: 2,
			}}
		>
			<Icon sx={{ fontSize: 28 }} />
		</Box>
		<Typography
			variant="subtitle1"
			sx={{
				color: "text.primary",
				fontWeight: 600,
				mb: 0.5,
				letterSpacing: "-0.01em",
			}}
		>
			{title}
		</Typography>
		{subtitle && (
			<Typography
				variant="body2"
				sx={{
					color: "text.secondary",
					maxWidth: 260,
					lineHeight: 1.5,
				}}
			>
				{subtitle}
			</Typography>
		)}
	</Box>
);

export const MeasurementsHomePage = ({
	initialSelectedMeasurement = null,
	isEmbedded = false,
}: Props) => {
	const theme = useTheme();
	const [selectedMeasurement, selectMeasurement] =
		useState<ManualMeasurementPointSummary | null>(null);
	const [groupId, setGroupId] = useState<number | null>(null);

	useEffect(() => {
		if (initialSelectedMeasurement) {
			selectMeasurement(initialSelectedMeasurement);
		}
	}, [initialSelectedMeasurement]);

	const dashboardHeader = isEmbedded ? null : (
		<Box
			sx={{
				display: "flex",
				flexDirection: { xs: "column", md: "row" },
				alignItems: { xs: "flex-start", md: "center" },
				justifyContent: "space-between",
				gap: 1.5,
				width: "100%",
			}}
		>
			<Box>
				<Typography
					variant="h4"
					component="h1"
					sx={{
						fontWeight: 700,
						letterSpacing: "-0.02em",
						color: "text.primary",
						lineHeight: 1.2,
					}}
				>
					<Translate String="Measurements.home.Header">Measurements</Translate>
				</Typography>
				<Typography
					variant="body2"
					sx={{
						mt: 0.5,
						color: "text.secondary",
						fontWeight: 500,
					}}
				>
					<Translate String="Measurements.home.Subtitle">
						Manage and track your measurement points
					</Translate>
				</Typography>
			</Box>
			<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
				<Chip
					size="small"
					icon={<InsightsRoundedIcon fontSize="small" />}
					label={
						selectedMeasurement?.Name ? (
							selectedMeasurement.Name
						) : (
							<Translate String="MeasurementsHomePage.NothingSelected">
								Nothing selected
							</Translate>
						)
					}
					variant="outlined"
				/>
				{groupId && (
					<Chip
						size="small"
						icon={<FolderOpenRoundedIcon fontSize="small" />}
						label={
							<>
								<Translate String="MeasurementsHomePage.Group">Group</Translate>
								: {groupId}
							</>
						}
						variant="outlined"
					/>
				)}
			</Stack>
		</Box>
	);

	return (
		<MeasurementContext.Provider
			value={{ selectedMeasurement, groupId, selectMeasurement, setGroupId }}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: "1 1 0%",
					minHeight: 0,
					overflow: "hidden",
					width: "100%",
				}}
			>
				<DashboardGrid
					dashboardHeader={dashboardHeader}
					buttons={<ButtonContainer />}
					gridAutoRows="minmax(140px, 1fr)"
					fullViewport
				>
					{/* Left: Groups tree — 4 cols on lg, full width on xs */}
					<Box
						sx={{
							gridColumn: { xs: "1 / -1", lg: "span 4" },
							gridRow: { xs: "auto", md: "1 / 5" },
							minHeight: { xs: 280, md: "auto" },
						}}
					>
						<Card
							sx={{
								...getPanelSx(theme),
								minHeight: "100%",
								display: "flex",
								flexDirection: "column",
							}}
							variant="outlined"
						>
							<CardContent
								sx={{
									p: 0,
									height: "100%",
									display: "flex",
									flexDirection: "column",
									"&:last-child": { pb: 0 },
								}}
							>
								<QueryErrorResetBoundary>
									{({ reset }) => (
										<Sentry.ErrorBoundary
											onReset={() => reset()}
											fallback={(boundary) => (
												<ComponentError reset={() => boundary.resetError()} />
											)}
										>
											<MeasurementGroupList />
										</Sentry.ErrorBoundary>
									)}
								</QueryErrorResetBoundary>
							</CardContent>
						</Card>
					</Box>

					{/* Center: Edit measurement — 3 cols on lg, 6 on sm */}
					<Box
						sx={{
							gridColumn: { xs: "1 / -1", sm: "span 6", lg: "span 3" },
							gridRow: { xs: "auto", md: "1 / 3" },
							minHeight: { xs: 200, md: "auto" },
						}}
					>
						<Card
							sx={{
								...getPanelSx(theme),
								display: "flex",
								flexDirection: "column",
								height: "100%",
							}}
							variant="outlined"
						>
							<CardContent
								sx={{
									p: 2,
									height: "100%",
									display: "flex",
									flexDirection: "column",
									overflowY: "auto",
									"&:last-child": { pb: 2 },
								}}
							>
								{!selectedMeasurement ? (
									<EmptyStatePlaceholder
										icon={TuneIcon}
										title={
											<Translate String="Measurements.SelectMeasurement">
												Select a measurement
											</Translate>
										}
										subtitle={
											<Translate String="Measurements.SelectMeasurementHint">
												Choose a measurement from the tree to view and edit
												details
											</Translate>
										}
										theme={theme}
									/>
								) : (
									<QueryErrorResetBoundary>
										{({ reset }) => (
											<Sentry.ErrorBoundary
												onReset={() => reset()}
												fallback={(boundary) => (
													<ComponentError reset={() => boundary.resetError()} />
												)}
											>
												<Suspense fallback={<EditMeasurementSkeleton />}>
													<EditMeasurement />
												</Suspense>
											</Sentry.ErrorBoundary>
										)}
									</QueryErrorResetBoundary>
								)}
							</CardContent>
						</Card>
					</Box>

					{/* Chart — 5 cols on lg, 6 on sm */}
					<Box
						sx={{
							gridColumn: { xs: "1 / -1", sm: "span 6", lg: "span 5" },
							gridRow: { xs: "auto", md: "1 / 3" },
							minHeight: { xs: 240, md: "auto" },
						}}
					>
						<Card
							sx={{
								...getPanelSx(theme),
								display: "flex",
								flexDirection: "column",
								height: "100%",
							}}
							variant="outlined"
						>
							<CardContent
								sx={{
									p: 2,
									height: "100%",
									width: "100%",
									display: "flex",
									flexDirection: "column",
									"&:last-child": { pb: 2 },
								}}
							>
								<QueryErrorResetBoundary>
									{({ reset }) => (
										<Sentry.ErrorBoundary
											onReset={() => reset()}
											fallback={(boundary) => (
												<ComponentError reset={() => boundary.resetError()} />
											)}
										>
											<MeasurementChartsContainer />
										</Sentry.ErrorBoundary>
									)}
								</QueryErrorResetBoundary>
							</CardContent>
						</Card>
					</Box>

					{/* Records table — spans remaining cols in rows 3–5 */}
					<Box
						sx={{
							gridColumn: { xs: "1 / -1", lg: "5 / -1" },
							gridRow: { xs: "auto", md: "3 / 5" },
							alignSelf: "stretch",
							minHeight: { xs: 300, md: "auto" },
						}}
					>
						<Card
							sx={{
								...getPanelSx(theme),
								height: "100%",
								display: "flex",
								flexDirection: "column",
							}}
							variant="outlined"
						>
							<CardContent
								sx={{
									p: 0,
									height: "100%",
									display: "flex",
									flexDirection: "column",
									"&:last-child": { pb: 0 },
								}}
							>
								<QueryErrorResetBoundary>
									{({ reset }) => (
										<Sentry.ErrorBoundary
											onReset={() => reset()}
											fallback={(boundary) => (
												<ComponentError reset={() => boundary.resetError()} />
											)}
										>
											<RecordsTable />
										</Sentry.ErrorBoundary>
									)}
								</QueryErrorResetBoundary>
							</CardContent>
						</Card>
					</Box>
				</DashboardGrid>
			</Box>
		</MeasurementContext.Provider>
	);
};
